import * as React from "react";
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardVehicleInventoryTabDetails } from "./TabDetails";
import { DashboardVehicleInventoryTabTransport } from "./TabTransport";
import { DashboardVehicleInventoryTabMedia } from "./TabMedia";
import { DashboardVehicleInventoryTabInspection } from "./TabInspection";
import { DashboardVehicleInventoryTabParts } from "./TabParts";
import { DashboardVehicleInventoryTabWork } from "./TabWork";
import { DashboardVehicleInventoryTabDocs } from "./TabDocs";
import {
    Button,
    Tabs,
    Typography,
    Avatar,
    Chip,
    IconButton,
    Popover,
    Dialog,
    Spinner,
} from "@material-tailwind/react";
import { MoreHoriz, Refresh } from "iconoir-react";

import { getVehicleStatusColor } from "../../../../utils/formatViews";
import { RootState } from "../../../../redux";
import { getVehicleRequest, updateVehicleRequest } from "../../../../redux/vehicles/actions";
import AppImages from "../../../../assets/AppImages";
import { listPartsRequest } from "../../../../redux/parts/actions";

const DETAILS_HEAD = [
    {
        comp: DashboardVehicleInventoryTabDetails,
        v: "details",
        n: "Details",
        i: AppImages.iconClipboardCheck,
        showToRoles: ["SuperUser", "Admin", "Parts Analyst"],
    },
    {
        comp: DashboardVehicleInventoryTabTransport,
        v: "transport",
        n: "Transport",
        i: AppImages.iconTruck,
        showToRoles: ["SuperUser", "Admin", "Parts Analyst"],
    },
    {
        comp: DashboardVehicleInventoryTabMedia,
        v: "media",
        n: "Media",
        i: AppImages.iconPhotograph,
        showToRoles: ["SuperUser", "Admin", "Parts Analyst"],
    },
    {
        comp: DashboardVehicleInventoryTabInspection,
        v: "inspections",
        n: "Inspections",
        i: AppImages.iconSearchCircle,
        // showToRoles: ["SuperUser", "Admin"],
    },
    {
        comp: DashboardVehicleInventoryTabParts,
        v: "part_list",
        n: "Part List",
        i: AppImages.iconKey,
        showToRoles: ["SuperUser", "Admin", "Mechanic", "Parts Analyst"],
    },
    {
        comp: DashboardVehicleInventoryTabWork,
        v: "work_orders",
        n: "Work Orders",
        i: AppImages.iconBadgeCheck,
        showToRoles: ["SuperUser", "Admin", "Mechanic"],
    },
    {
        comp: DashboardVehicleInventoryTabDocs,
        v: "documents",
        n: "Titles/Documents",
        i: AppImages.iconDocumentText,
        showToRoles: ["SuperUser", "Admin", "Parts Analyst"],
    },
];

export function DashboardVehicleInventoryDetails() {
    const dispatch = useDispatch();
    const vin = useParams().vin;
    const navigate = useNavigate();
    const { vehicle, loading } = useSelector((state: RootState) => state.vehicles);
    const [activeTab, setActiveTab] = React.useState<string>("");
    const [defaultTab, setDefaultTab] = React.useState<string>("");
    const { auth } = useSelector((state: RootState) => state.auth);

    const handleRefresh = () => {
        dispatch(
            getVehicleRequest({
                vin: vin,
                vehicle_fields: "",
            })
        );
    };

    React.useEffect(() => {
        if (auth?.user?.role?.title) {
            if (["SuperUser", "Admin", "Parts Analyst"].includes(auth?.user?.role?.title || "")) {
                setActiveTab("details");
                setDefaultTab("details");
            } else if (["Mechanic"].includes(auth?.user?.role?.title || "")) {
                setActiveTab("work_orders");
                setDefaultTab("work_orders");
            }
        }
    }, [auth]);

    React.useEffect(() => {
        handleRefresh();
    }, [dispatch, vin]);

    React.useEffect(() => {
        if (vehicle?.id) {
            if (vehicle?.id === -1) {
                navigate("/dashboard/vehicles/inventory");
                return;
            }
            dispatch(listPartsRequest({
                filters: `vehicle_id:${vehicle?.id}`,
                part_fields: "",
                excludes: "",
            }));
        }
    }, [dispatch, vehicle]);

    return (
        <React.Fragment>
            <Dialog open={loading}>
                <Dialog.Overlay>
                    <div className="flex flex-col justify-center items-center h-full">
                        <Spinner size="xxl" />
                    </div>
                </Dialog.Overlay>
            </Dialog>
            <div className="flex mt-5 justify-between items-center">
                <div className="flex gap-x-5 items-center relative">
                    <Button
                        color="secondary"
                        className="bg-white px-3 py-3 text-primary"
                        onClick={() => {
                            try {
                                navigate(-1);
                            } catch {
                                navigate('/dashboard/vehicles/inventory');
                            }
                        }}
                    >
                        <AppImages.iconArrowLeft className="h-3 w-3" />
                    </Button>

                    <div className="items-center flex gap-x-3">
                        {vehicle?.thumbnail?.full_url && (
                            <Avatar
                                src={vehicle?.thumbnail.full_url}
                                alt={vehicle?.ymmt}
                                shape="rounded"
                            />
                        )}
                        <div className="flex flex-col gap-x-3">
                            <Typography
                                type="small"
                                color="default"
                                className="md:text-lg font-bold text-primary"
                            >
                                {vehicle?.ymmt}
                            </Typography>
                            <Typography type="small" color="default" className="text-foreground">
                                {vehicle?.vin}
                            </Typography>
                        </div>
                        {vehicle?.status && (
                            <Chip
                                size="sm"
                                isPill={false}
                                className="w-max"
                                color={getVehicleStatusColor(vehicle.status)}
                            >
                                <Chip.Label className="capitalize font-bold">
                                    {vehicle.display_status}
                                </Chip.Label>
                            </Chip>
                        )}
                    </div>
                </div>
                <div className="flex gap-x-2">
                    <Button
                        variant="outline"
                        color="secondary"
                        className="flex items-center gap-2 sm:w-auto w-full"
                        onClick={() => {
                            handleRefresh();
                        }}
                    >
                        <Refresh className="w-4 h-4" />
                    </Button>
                    <Popover placement="bottom-end">
                        <Popover.Trigger
                            as={IconButton}
                            variant="outline"
                            color="secondary"
                            className="h-5 bg-white"
                        >
                            <MoreHoriz className="w-4 h-4" />
                        </Popover.Trigger>
                        <Popover.Content className="-mt-1 flex flex-col gap-y-2">
                            <Button
                                variant="outline"
                                color="secondary"
                                className="h-8 flex items-center !py-1 gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    dispatch(
                                        updateVehicleRequest({
                                            vehicle: {
                                                ...vehicle,
                                                is_sold: true,
                                                vehicle,
                                            },
                                            update_fields: ["id", "is_sold"],
                                            vehicle_fields: "",
                                        })
                                    );
                                }}
                            >
                                Mark as Sold
                            </Button>
                            <Button
                                variant="outline"
                                color="error"
                                className="h-8 flex items-center !py-1 gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    withReactContent(Swal)
                                        .fire({
                                            title: "Are you sure?",
                                            text: "This action will permanently delete the vehicle.",
                                            icon: "warning",
                                            showCancelButton: true,
                                            confirmButtonColor: "#EF4444",
                                            confirmButtonText: "Remove",
                                        })
                                        .then((result) => {
                                            if (result.isConfirmed) {
                                                dispatch(
                                                    updateVehicleRequest({
                                                        vehicle: {
                                                            ...vehicle,
                                                            is_removed: true,
                                                        },
                                                        update_fields: ["id", "is_removed"],
                                                        vehicle_fields: "",
                                                    })
                                                );
                                                navigate("/dashboard/vehicles/inventory");
                                            }
                                        })
                                        .catch((error: any) => {
                                            console.log("error", error);
                                        });
                                }}
                            >
                                Delete
                                <AppImages.iconTrash className="w-4 h-4" />
                            </Button>
                        </Popover.Content>
                    </Popover>
                </div>
            </div>
            <Tabs
                defaultValue={defaultTab}
                value={activeTab}
                className="mt-5"
                onValueChange={setActiveTab}
            >
                <Tabs.List className="w-max max-w-full overflow-x-auto">
                    {DETAILS_HEAD.filter((el) => {
                        return (
                            !el.showToRoles ||
                            el.showToRoles.includes(auth?.user?.role?.title || "")
                        );
                    }).map((el, idx) => (
                        <Tabs.Trigger
                            key={idx}
                            className={`w-max px-4 py-2 ${
                                el.v === activeTab ? "text-primary" : "text-[#64748B]"
                            }`}
                            value={el.v}
                        >
                            <el.i className="mr-2 h-4 w-4" />
                            <span className="text-nowrap">{el.n}</span>
                        </Tabs.Trigger>
                    ))}
                    <Tabs.TriggerIndicator />
                </Tabs.List>
                {DETAILS_HEAD.filter((el) => {
                    return (
                        !el.showToRoles || el.showToRoles.includes(auth?.user?.role?.title || "")
                    );
                }).map((el, idx) => (
                    <Tabs.Panel key={idx} value={el.v}>
                        {el.comp && <el.comp />}
                    </Tabs.Panel>
                ))}
            </Tabs>
        </React.Fragment>
    );
}
